import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TablePagination,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DocumentTypeFilters from "../../../components/Admin/DocumentTypes/DocumentTypeFilters";
import DocumentTypeList from "../../../components/Admin/DocumentTypes/DocumentTypeList";
import DocumentTypeForm from "../../../components/Admin/DocumentTypes/DocumentTypeForm";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../auth";
import {
  getListSubDocumentTypeQueryKey,
  getListSubDocumentTypeQueryOptions,
  getEnableSubDocumentTypeMutationOptions,
  getDisableSubDocumentTypeMutationOptions,
  getCreateSubDocumentTypeMutationOptions,
  getEditSubDocumentTypeMutationOptions,
  getDeleteSubDocumentTypeMutationOptions,
} from "../../../api/documentos/endpoints";
import type { SubDocumentTypeDto } from "../../../api/documentos/model";

const documentTypesSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

export const Route = createFileRoute("/__auth/admin/document-types")({
  component: DocumentTypesComponent,
  validateSearch: (search) => documentTypesSearchSchema.parse(search),
  staticData: {
    title: "documentTypes.title",
  },
});

function DocumentTypesComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { currentTenant } = useAuth();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SubDocumentTypeDto | null>(
    null,
  );
  const [itemToDelete, setItemToDelete] = useState<SubDocumentTypeDto | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const effectiveTenant = currentTenant || "default-domain";
  const documentTypesQueryKey = getListSubDocumentTypeQueryKey(effectiveTenant);

  const refreshDocumentTypes = () =>
    queryClient.invalidateQueries({
      queryKey: documentTypesQueryKey,
    });

  const { data, isLoading, error } = useQuery({
    ...getListSubDocumentTypeQueryOptions(effectiveTenant, {
      isPageable: true,
      q: search.q || undefined,
      page: (search.page || 1) - 1,
      size: search.size || 10,
    } as any),
    staleTime: 30000,
  });

  const rows: SubDocumentTypeDto[] = useMemo(() => {
    const content = (data as any)?.data || data;
    return (content as any)?.content ?? (Array.isArray(content) ? content : []);
  }, [data]);

  const totalElements = useMemo(() => {
    const content = (data as any)?.data || data;
    return (content as any)?.totalElements ?? rows.length;
  }, [data, rows.length]);

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1,
      }),
    });
  };

  const enableMut = useMutation({
    ...getEnableSubDocumentTypeMutationOptions(),
    onSuccess: () => {
      refreshDocumentTypes();
      showSnackbar(t("documentTypes.statusSuccess"));
    },
    onError: () => showSnackbar(t("documentTypes.statusError"), "error"),
  });

  const disableMut = useMutation({
    ...getDisableSubDocumentTypeMutationOptions(),
    onSuccess: () => {
      refreshDocumentTypes();
      showSnackbar(t("documentTypes.statusSuccess"));
    },
    onError: () => showSnackbar(t("documentTypes.statusError"), "error"),
  });

  const deleteMutation = useMutation({
    ...getDeleteSubDocumentTypeMutationOptions(),
    onSuccess: () => {
      refreshDocumentTypes();
      showSnackbar(t("documentTypes.deleteSuccess"));
    },
    onError: () => showSnackbar(t("documentTypes.deleteError"), "error"),
  });

  const handleApiError = (error: any) => {
    const apiMessage = error?.response?.data?.message || error?.message || "";
    let errorMessage = t("documentTypes.saveError");

    if (apiMessage) {
      if (
        apiMessage.includes("título do tipo de documento é repetido") ||
        apiMessage.includes("repetido")
      ) {
        errorMessage = t("documentTypes.errors.titleRepeated");
      } else if (apiMessage.includes("campos estão inválidos")) {
        errorMessage = t("documentTypes.errors.invalidFields");
      } else {
        errorMessage = apiMessage;
      }
    }

    showSnackbar(errorMessage, "error");
  };

  const createMutation = useMutation({
    ...getCreateSubDocumentTypeMutationOptions(),
    onSuccess: () => {
      refreshDocumentTypes();
      showSnackbar(t("documentTypes.createSuccess"));
      setIsDialogOpen(false);
    },
    onError: (error) => handleApiError(error),
  });

  const editMutation = useMutation({
    ...getEditSubDocumentTypeMutationOptions(),
    onSuccess: () => {
      refreshDocumentTypes();
      showSnackbar(t("documentTypes.updateSuccess"));
      setIsDialogOpen(false);
    },
    onError: (error) => handleApiError(error),
  });

  return (
    <ListLayout
      title={t("documentTypes.title")}
      subtitle={t("documentTypes.subtitle")}
      filters={
        <DocumentTypeFilters
          onApply={handleFilter}
          isLoading={isLoading}
          initialValues={{ q: search.q }}
        />
      }
      isLoading={isLoading}
      isEmpty={!rows.length}
      error={error as any}
      actions={
        <IconButton
          color="primary"
          onClick={() => {
            setSelectedItem(null);
            setIsDialogOpen(true);
          }}
        >
          <AddIcon />
        </IconButton>
      }
    >
      <DocumentTypeList
        data={rows}
        isLoading={isLoading}
        onEdit={(item) => {
          setSelectedItem(item);
          setIsDialogOpen(true);
        }}
        onToggleEnable={(item) => {
          if (item.enabled)
            disableMut.mutate({
              tenant: effectiveTenant,
              subDocumentTypeId: item.id!,
            });
          else
            enableMut.mutate({
              tenant: effectiveTenant,
              subDocumentTypeId: item.id!,
            });
        }}
        onDelete={(item) => {
          setItemToDelete(item);
          setIsDeleteDialogOpen(true);
        }}
      />

      <TablePagination
        component="div"
        count={totalElements}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true,
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t("common.of")} ${count}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      />

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <DocumentTypeForm
            initialValues={selectedItem || undefined}
            onSubmit={(vals) => {
              if (selectedItem?.id) {
                editMutation.mutate({
                  tenant: effectiveTenant,
                  subDocumentTypeId: selectedItem.id,
                  data: vals,
                });
              } else {
                createMutation.mutate({ tenant: effectiveTenant, data: vals });
              }
            }}
            onCancel={() => setIsDialogOpen(false)}
            isLoading={createMutation.isPending || editMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("documentTypes.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("documentTypes.deleteConfirm", { name: itemToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (itemToDelete?.id) {
                deleteMutation.mutate({
                  tenant: effectiveTenant,
                  subDocumentTypeId: itemToDelete.id,
                });
              }
              setIsDeleteDialogOpen(false);
              setItemToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
